/**
 * Ручна привʼязка документа до іншого (ч.1220, рішення власника: «в доках меню Комерція
 * в блоці звʼязаних документів дати можливість привʼязати док до іншого доку реалізації
 * або покупки, а також ПН + оплата»).
 *
 * Звʼязок — інформаційний (таблиця звʼязків, як і перенесені з 1С, ч.1219): облік, резерви
 * і розрахунки не змінює, тому працює і на проведених документах. Кандидатів шукаємо
 * зведеними журналами «Комерції» (документи покупців / постачальників) — там уже є всі
 * види, права на кожен вид і пошук за контрагентом та номером.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import {
  commerceJournal, type CommerceJournalRow, type CommerceJournalSide,
} from '@/api/commerceJournal';
import BodyPortal from './BodyPortal';
import type { DtKtDocRef } from './DtKtJournal';
import TchTable from '@/shared/components/TchTable';

/** Документи, для яких працює привʼязка (перелік видів зведених журналів «Комерції»);
 *  остаточно вирішує сервер — GET /documents/links/context. */
export const LINKABLE_DOC_TYPES: ReadonlySet<string> = new Set([
  'doc.customer_orders', 'doc.goods_sales', 'doc.service_acts', 'doc.production_service_acts',
  'doc.tnn', 'doc.goods_returns_from_customer',
  'doc.supplier_orders', 'doc.goods_receipts', 'doc.additional_expenses',
  'doc.customs_declarations', 'doc.goods_returns_to_supplier', 'doc.powers_of_attorney',
  'doc.bank_receipts', 'doc.bank_payments', 'doc.cash_receipts', 'doc.cash_payments',
  'tax.tax_invoices_outgoing', 'tax.tax_invoices_incoming',
]);

export interface LinkContext {
  /** Тип документа підтримує привʼязку. */
  linkable: boolean;
  /** Користувач може змінювати цей документ (право розділу в організації документа). */
  can_link: boolean;
  /** Чому привʼязати не можна (для підказки на кнопці). */
  reason: string | null;
  organization_id: string | null;
  contractor_id: string | null;
  contractor_name: string | null;
  /** З якого журналу почати пошук. */
  side: CommerceJournalSide;
}

export function useLinkContext(doc: DtKtDocRef, enabled = true) {
  const docId = doc.id && doc.id !== 'new' ? doc.id : undefined;
  return useQuery({
    queryKey: ['doc-link-context', doc.type, docId],
    queryFn: () => api.get<LinkContext>('/documents/links/context', {
      params: { document_type: doc.type, document_id: docId },
    }).then((r) => r.data),
    enabled: enabled && !!docId && LINKABLE_DOC_TYPES.has(doc.type),
    staleTime: 60_000,
  });
}

function errorText(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
}

/** Прибрати ручний звʼязок (кнопка «відвʼязати» у попапі звʼязаних документів). */
export function useUnlinkDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => api.delete(`/documents/links/${linkId}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['related-docs'] }); },
  });
}

const SIDES: { side: CommerceJournalSide; label: string }[] = [
  { side: 'sales', label: 'документи покупців' },
  { side: 'purchases', label: 'документи постачальників' },
];
const PAGE = 50;

function rowDate(row: Pick<CommerceJournalRow, 'document_date' | 'has_time'>): string {
  if (!row.document_date) return '—';
  if (!row.has_time) {
    const [y, m, d] = row.document_date.slice(0, 10).split('-');
    return `${d}.${m}.${y}`;
  }
  return new Date(row.document_date).toLocaleDateString('uk-UA');
}

function rowAmount(v: string | null): string {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v;
}

export function LinkDocumentDialog({ doc, ctx, linkedKeys, onClose }: {
  doc: DtKtDocRef;
  ctx: LinkContext;
  /** Уже звʼязані документи («таблиця:id») — щоб не пропонувати їх удруге. */
  linkedKeys: ReadonlySet<string>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [side, setSide] = useState<CommerceJournalSide>(ctx.side);
  const [kind, setKind] = useState('');
  const [sameContractor, setSameContractor] = useState(!!ctx.contractor_id);
  const [number, setNumber] = useState('');
  const [q, setQ] = useState('');
  const [justLinked, setJustLinked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Пошук за номером — із затримкою, щоб не слати запит на кожну літеру.
  useEffect(() => {
    const t = setTimeout(() => setQ(number.trim()), 300);
    return () => clearTimeout(t);
  }, [number]);

  const list = useQuery({
    queryKey: ['doc-link-candidates', side, kind, sameContractor ? ctx.contractor_id : null, q,
      ctx.organization_id],
    queryFn: () => commerceJournal.list(side, {
      organization_id: ctx.organization_id ?? undefined,
      contractor_id: sameContractor && ctx.contractor_id ? ctx.contractor_id : undefined,
      kind: kind ? [kind] : undefined,
      q: q || undefined,
      sort_by: 'date', sort_dir: 'desc', limit: PAGE, offset: 0,
    }),
    enabled: !!ctx.organization_id,
  });

  const link = useMutation({
    mutationFn: (row: CommerceJournalRow) => api.post('/documents/links', {
      document_type: doc.type, document_id: doc.id,
      linked_type: row.document_type, linked_id: row.id,
    }),
    onMutate: () => setError(null),
    onSuccess: (_r, row) => {
      setJustLinked((prev) => new Set(prev).add(`${row.document_type}:${row.id}`));
      void qc.invalidateQueries({ queryKey: ['related-docs'] });
    },
    onError: (e) => setError(errorText(e, 'Не вдалося привʼязати документ.')),
  });

  const rows = (list.data?.items ?? []).filter(
    (r) => !(r.document_type === doc.type && r.id === doc.id));
  const kinds = list.data?.kinds ?? [];
  const forbidden = (list.error as { response?: { status?: number } } | null)?.response?.status === 403;

  return (
    <BodyPortal>
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'var(--overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div className="card" role="dialog" aria-label="Привʼязати документ"
        style={{ width: 860, maxWidth: '96vw', maxHeight: '82vh', overflowY: 'auto', margin: 0 }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem', gap: '0.4rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Привʼязати документ</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
          Звʼязок лише показує документи поруч у блоці «Звʼязані документи» — проведення,
          розрахунки і залишки не змінює.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center',
          marginBottom: '0.5rem' }}>
          <div role="group" aria-label="Журнал" style={{ display: 'flex', gap: '0.25rem' }}>
            {SIDES.map((s) => (
              <button key={s.side} type="button" aria-pressed={side === s.side}
                className={side === s.side ? 'primary' : undefined}
                onClick={() => { setSide(s.side); setKind(''); }}>
                {s.label}
              </button>
            ))}
          </div>
          <select aria-label="Вид документа" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">усі види документів</option>
            {kinds.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
          </select>
          <input aria-label="Номер документа" placeholder="номер документа" value={number}
            onChange={(e) => setNumber(e.target.value)} style={{ width: 170 }} />
          {ctx.contractor_id && (
            <label style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={sameContractor}
                onChange={(e) => setSameContractor(e.target.checked)} />
              лише контрагент документа{ctx.contractor_name ? ` (${ctx.contractor_name})` : ''}
            </label>
          )}
        </div>
        {error && <div role="alert" style={{ color: 'var(--badge-red-fg)', marginBottom: '0.4rem' }}>{error}</div>}
        {list.isLoading && <div style={{ color: 'var(--muted)' }}>Завантаження…</div>}
        {forbidden && (
          <div style={{ color: 'var(--muted)' }}>Недостатньо прав для перегляду цього журналу.</div>
        )}
        {list.isError && !forbidden && (
          <div style={{ color: 'var(--badge-red-fg)' }}>Не вдалося завантажити документи.</div>
        )}
        {list.data && rows.length === 0 && (
          <div style={{ color: 'var(--muted)' }}>Документів за цим відбором немає.</div>
        )}
        {rows.length > 0 && (
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 90 }}>Дата</th>
                <th style={{ width: 130 }}>№</th>
                <th style={{ textAlign: 'left' }}>Вид документа</th>
                <th style={{ width: 110, textAlign: 'right' }}>Сума</th>
                <th style={{ textAlign: 'left' }}>Контрагент</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = `${r.document_type}:${r.id}`;
                const already = linkedKeys.has(key) || justLinked.has(key);
                return (
                  <tr key={key}>
                    <td>{rowDate(r)}</td>
                    <td>{r.document_number ?? '—'}</td>
                    <td>{r.kind_label}</td>
                    <td style={{ textAlign: 'right' }}>{rowAmount(r.amount)}</td>
                    <td>{r.contractor_name ?? ''}</td>
                    <td style={{ textAlign: 'center' }}>
                      {already
                        ? <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>уже звʼязано</span>
                        : (
                          <button type="button" disabled={link.isPending}
                            onClick={() => link.mutate(r)}>привʼязати</button>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
        )}
        {list.data && list.data.total > PAGE && (
          <div style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: '0.4rem' }}>
            Показано перші {PAGE} з {list.data.total} — уточніть вид, номер або контрагента.
          </div>
        )}
      </div>
    </div>
    </BodyPortal>
  );
}

/** Кнопка «привʼязати»: видно лише в документах, які підтримують привʼязку; без права
 *  змінювати документ — неактивна з підказкою (Закон дизайну #6: не ховати, а пояснити). */
export function LinkDocumentButton({ doc, linkedKeys, className }: {
  doc: DtKtDocRef;
  linkedKeys: ReadonlySet<string>;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ctx = useLinkContext(doc);
  if (!ctx.data?.linkable) return null;
  const allowed = ctx.data.can_link;
  return (
    <>
      <button type="button" className={className} disabled={!allowed}
        title={allowed
          ? 'Привʼязати до документа продажу або закупівлі, податкової накладної чи оплати'
          : (ctx.data.reason ?? 'Недостатньо прав, щоб змінювати цей документ')}
        onClick={() => setOpen(true)}>
        привʼязати
      </button>
      {open && allowed && (
        <LinkDocumentDialog doc={doc} ctx={ctx.data} linkedKeys={linkedKeys}
          onClose={() => setOpen(false)} />
      )}
    </>
  );
}
